import { useContext, useState } from 'react'

import dayjs from 'dayjs'

import {
  useCustomerBehaviorAnalyticsGetApi,
  useCustomerInsightGetApi
} from '@/common/api/modules/customer'
import {
  BehaviorAnalysisResponse,
  BehaviorTimeline,
  BehaviorTimelineActionType,
  GenerateCustomerInsightVars
} from '@/common/api/params/customer'
import { currencyText } from '@/common/app/currency'
import { behaviorTimelineActionTypeName } from '@/common/app/modules/recommend/behaviorLog'
import { widgetDisplayLogicName } from '@/common/app/modules/recommend/widget'
import { widgetEditPath } from '@/common/app/path'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import History from '@/components/view/atoms/history/History'
import AiLoading from '@/components/view/atoms/loading/AiLoading'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import Line from '@/components/view/layouts/area/Line'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import AiLoadingArea from '@/components/view/layouts/loading/AiLoadingArea'
import Modal from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ListFormLoading from '@/components/view/molecules/loading/ListFormLoading'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'
import Markdown from '@/components/view/utils/markdown/Markdown'

import { AccessCustomerModalContext } from './AccessCustomerModalProvider'

type TimelineEvent =
  | { type: '_session'; text: string }
  | { type: '_revisit'; text: string }
  | { type: '_device_change'; text: string }

const CAT_COLORS: Record<string, string> = {
  pain_point: '#dc2626',
  emotional_gain: '#d97706',
  target_persona: '#db2777',
  buying_motive: '#4f46e5',
  usage_scene: '#0d9488',
  purchase_intent: '#7c3aed',
  feature: '#059669',
  product_strength: '#ea580c',
  material: '#6d28d9',
  season: '#2563eb',
  occasion: '#0891b2'
}

interface InsightTextProps {
  vars: GenerateCustomerInsightVars
}

const InsightText = ({ vars }: InsightTextProps) => {
  const {
    data: insightData,
    isLoading,
    error: insightError,
    mutate
  } = useCustomerInsightGetApi(vars)

  return (
    <>
      {isLoading && (
        <AiLoadingArea>
          <AiLoading description='インサイトの分析中です' />
        </AiLoadingArea>
      )}
      {insightData && (
        <AiInsideArea>
          <AreaContent>
            <Markdown size={14}>{insightData.insight}</Markdown>
          </AreaContent>
        </AiInsideArea>
      )}
      {insightError && (
        <AiInsideArea>
          <div className='py-6 text-center'>
            <Text size={12} color={TextColor.CRITICAL}>
              インサイトの取得に失敗しました
            </Text>
            <div className='mt-4 flex justify-center'>
              <Button onClick={() => mutate()} color={ButtonColor.PRIMARY_BRAND}>
                再試行
              </Button>
            </div>
          </div>
        </AiInsideArea>
      )}
    </>
  )
}

const TimelineProduct = ({ item }: { item: BehaviorTimeline }) => {
  return (
    <ItemAlign gapX={2}>
      <Badge size={12} badgeType={BadgeType.SQUARE_TERTIARY}>
        商品
      </Badge>
      <ItemAlign gapX={16}>
        {item.item_id && <Text size={12}>{item.item_id}</Text>}
        {item.product_name && <Text size={12}>{item.product_name}</Text>}
      </ItemAlign>
      {item.product_image && (
        <img src={item.product_image} alt='' className='h-7 w-7 rounded object-cover' />
      )}
    </ItemAlign>
  )
}

interface TimelineItemProps {
  item: BehaviorTimeline
}

interface TimelineItemProps {
  item: BehaviorTimeline
  siteId: number
}

const TimelineItem = ({ item, siteId }: TimelineItemProps) => {
  return (
    (item.type === 'behavior' ||
      item.type === 'delivery' ||
      item.type === 'rec_click' ||
      item.type === 'order') && (
      <ItemAlign gapX={16}>
        {item.type === 'behavior' && item.action === 'view' && item.page_url && (
          <TextButton link={item.page_url} size={12} newTab>
            {item.page_url}
          </TextButton>
        )}
        {item.type === 'behavior' && item.action === 'view_product' && (
          <TimelineProduct item={item} />
        )}
        {item.type === 'behavior' && item.action === 'cart' && item.item_id && (
          <TimelineProduct item={item} />
        )}
        {item.type === 'delivery' && (
          <ItemAlign gapX={4}>
            <Badge size={12} badgeType={BadgeType.SQUARE_TERTIARY}>
              レコメンドパーツ
            </Badge>
            {item.spec_id ? (
              <TextButton link={widgetEditPath(siteId, Number(item.spec_id))} size={12} newTab>
                {item.display_logic
                  ? widgetDisplayLogicName(item.display_logic)
                  : ' ' + item.spec_id}
              </TextButton>
            ) : (
              <Text size={12}>
                {item.display_logic + (item.widget_id ? ` ${item.widget_id}` : '')}
              </Text>
            )}
          </ItemAlign>
        )}
        {item.type === 'rec_click' && item.item_id && <TimelineProduct item={item} />}
        {item.type === 'order' && (
          <ItemAlign gapX={8}>
            {item.unit_price != null && (
              <ItemAlign gapX={2}>
                <Badge size={12} badgeType={BadgeType.SQUARE_CRITICAL}>
                  受注
                </Badge>
                {item.order_number != null && <Text size={12}>{item.order_number}</Text>}
              </ItemAlign>
            )}
            {item.unit_price != null && (
              <ItemAlign gapX={2}>
                <Badge size={12} badgeType={BadgeType.SQUARE_TERTIARY}>
                  金額
                </Badge>
                <Text size={12}>{currencyText(item.unit_price)}</Text>
              </ItemAlign>
            )}
            {item.quantity != null && (
              <ItemAlign gapX={2}>
                <Badge size={12} badgeType={BadgeType.SQUARE_TERTIARY}>
                  数量
                </Badge>
                <Text size={12}>{item.quantity}</Text>
              </ItemAlign>
            )}
            <TimelineProduct item={item} />
          </ItemAlign>
        )}
      </ItemAlign>
    )
  )
}

const divideTimelines = (timelines: BehaviorTimeline[]): (TimelineEvent | BehaviorTimeline[])[] => {
  let prevSessionId: string | null | undefined = null
  let prevDeviceType: string | null | undefined = null
  let prevBrowser: string | null | undefined = null
  let prevTime: dayjs.Dayjs | null = null

  const dividedTimelines = timelines.reduce(
    (items, timeline) => {
      const curDate = dayjs(timeline.timestamp)
      if (
        timeline.type === 'behavior' &&
        timeline.session_id &&
        timeline.session_id !== prevSessionId &&
        prevSessionId !== null
      ) {
        if (prevTime && curDate) {
          const diff = curDate.diff(prevTime, 'minute')
          if (diff >= 30) {
            const hours = Math.floor(diff / 60)
            const mins = diff % 60
            const timeStr = hours > 0 ? `${hours}時間${mins > 0 ? mins + '分' : ''}` : `${mins}分`
            items.push({ type: '_revisit', text: `${timeStr}後に再訪問` })
          }
        }
        if (
          prevDeviceType &&
          timeline.device_type &&
          (timeline.device_type !== prevDeviceType || timeline.browser !== prevBrowser)
        ) {
          items.push({
            type: '_device_change',
            text: `デバイス変更: ${timeline.device_type} / ${timeline.browser}`
          })
        }
        items.push({
          type: '_session',
          text: `セッション ${timeline.session_id} (${timeline.device_type || '?'} / ${timeline.browser || '?'})`
        })
      } else if (timeline.type === 'behavior' && timeline.session_id && prevSessionId === null) {
        items.push({
          type: '_session',
          text: `セッション ${timeline.session_id} (${timeline.device_type || '?'} / ${timeline.browser || '?'})`
        })
      }

      items.push(timeline)

      if (timeline.type === 'behavior') {
        prevSessionId = timeline.session_id
        prevDeviceType = timeline.device_type
        prevBrowser = timeline.browser
      }
      if (curDate) {
        prevTime = curDate
      }

      return items
    },
    [] as (TimelineEvent | BehaviorTimeline)[]
  )

  return dividedTimelines.reduce(
    (result, item) => {
      if (item.type === '_session' || item.type === '_revisit' || item.type === '_device_change') {
        result.push(item)
      } else if (result.length > 0) {
        if ((result[result.length - 1] as TimelineEvent).type) {
          result.push([item] as BehaviorTimeline[])
        } else {
          ;(result[result.length - 1] as BehaviorTimeline[]).push(item)
        }
      } else {
        result.push([item] as BehaviorTimeline[])
      }
      return result
    },
    [] as (TimelineEvent | BehaviorTimeline[])[]
  )
}

const Title = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className='px-4 py-2'>
      <Text size={16} weight='bold' tag='h3'>
        {children}
      </Text>
    </div>
  )
}

interface BehaviorAnalysisModalProps {
  data: BehaviorAnalysisResponse & { _customer_id: string | null }
  siteId: number
}

const BehaviorAnalysisModalInner = ({ data, siteId }: BehaviorAnalysisModalProps) => {
  const totalDeviceEvents = Object.values(data.device_counts || {}).reduce((a, b) => a + b, 0)
  const deviceParts = Object.entries(data.device_counts || {})
    .map(
      ([k, v]) =>
        `${k.toUpperCase()}: ${totalDeviceEvents ? Math.round((v / totalDeviceEvents) * 100) : 0}%`
    )
    .join(' / ')

  const totalPurchaseDevices = Object.values(data.purchase_device_counts || {}).reduce(
    (a, b) => a + b,
    0
  )
  const purchaseDeviceParts = Object.entries(data.purchase_device_counts || {})
    .map(
      ([k, v]) =>
        `${k.toUpperCase()}: ${totalPurchaseDevices ? Math.round((v / totalPurchaseDevices) * 100) : 0}%`
    )
    .join(' / ')

  const [isInsightAnalyzing, setIsInsightAnalyzing] = useState<boolean>(false)

  const timelineGroups = divideTimelines(data.timeline || [])

  return (
    <AreaContent>
      <ItemAlign gapX={16}>
        <ItemAlign gapX={2}>
          <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={12}>
            クッキーID
          </Badge>
          <Text size={16}>{data.visitor_id}</Text>
        </ItemAlign>
        <ItemAlign gapX={2}>
          <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={12}>
            顧客ID
          </Badge>
          <Text size={16}>{data._customer_id || '未連携'}</Text>
        </ItemAlign>
        <ItemAlign gapX={2}>
          <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={12}>
            デバイス利用比率
          </Badge>
          <Text size={16}>{deviceParts || '-'}</Text>
        </ItemAlign>
        <ItemAlign gapX={2}>
          <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={12}>
            購入手段の比率
          </Badge>
          <Text size={16}>{purchaseDeviceParts || '-'}</Text>
        </ItemAlign>
      </ItemAlign>
      <Area noBorder bgColor='secondary'>
        <SummaryItemList>
          {[
            { num: data.total_behavior_events, label: '総行動' },
            { num: data.total_deliveries, label: '配信' },
            { num: data.total_rec_clicks, label: 'クリック' },
            { num: data.total_orders, label: '注文' },
            { num: data.views_before_first_purchase, label: '購入前閲覧' }
          ].map(({ num, label }) => (
            <SummaryItem key={label}>
              <Text weight='bold'>{label}</Text>
              <ItemAlign>
                <Text weight='bold' size={24}>
                  {num}
                </Text>
                <Text size={14}>件</Text>
              </ItemAlign>
            </SummaryItem>
          ))}
        </SummaryItemList>
      </Area>
      <Line />
      <div className='flex gap-2'>
        <div className='w-full'>
          <Title>タイムライン</Title>
          <>
            {timelineGroups.length === 0 ? (
              <Text size={12} color={TextColor.SECONDARY}>
                行動データがありません
              </Text>
            ) : (
              <InsideArea color={BgColor.SECONDARY}>
                {timelineGroups.map((group, index) =>
                  (group as TimelineEvent).type ? (
                    <Text
                      key={index}
                      size={12}
                      color={
                        (group as TimelineEvent).type == '_revisit'
                          ? TextColor.SUCCESS
                          : TextColor.PRIMARY
                      }
                      weight='bold'
                    >
                      {(group as TimelineEvent).text}
                    </Text>
                  ) : (
                    <History
                      key={index}
                      list={(group as BehaviorTimeline[]).map((item) => ({
                        date: item.timestamp ?? '',
                        title: (
                          <Badge
                            size={12}
                            badgeType={
                              item.action == BehaviorTimelineActionType.RECOMMENDATION_CLICK ||
                              item.action == BehaviorTimelineActionType.RECOMMENDATION_DELIVERY
                                ? BadgeType.SQUARE_AI
                                : BadgeType.SQUARE_SECONDARY
                            }
                          >
                            {behaviorTimelineActionTypeName(item.action)}
                          </Badge>
                        ),
                        message: <TimelineItem item={item} siteId={siteId} />
                      }))}
                    />
                  )
                )}
              </InsideArea>
            )}
          </>
        </div>
        <div className='w-full'>
          <Title>顧客インサイトAI分析</Title>
          <>
            {isInsightAnalyzing ? (
              <InsightText
                vars={{
                  site_id: siteId,
                  visitor_id: data.visitor_id,
                  timeline: data.timeline || [],
                  summary: {
                    total_behavior_events: data.total_behavior_events,
                    total_orders: data.total_orders,
                    total_deliveries: data.total_deliveries,
                    total_rec_clicks: data.total_rec_clicks,
                    views_before_first_purchase: data.views_before_first_purchase
                  }
                }}
              />
            ) : (
              <AiInsideArea border>
                <div className='py-6 text-center'>
                  <Text size={12} color={TextColor.AI} weight='bold'>
                    AIがタイムラインから
                    <br />
                    顧客の行動パターンと購買意欲を分析します
                  </Text>
                  <div className='mt-4 flex justify-center'>
                    <AiButton size='large' onClick={() => setIsInsightAnalyzing(true)}>
                      AIで分析する
                    </AiButton>
                  </div>
                </div>
              </AiInsideArea>
            )}
            {data.ai_tags_interest && data.ai_tags_interest.length > 0 && (
              <AiInsideArea border>
                <div className='border-secondary mt-4 flex flex-col gap-1 border-t pt-4'>
                  <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                    興味関心（AI解析タグ）
                  </Text>
                  <div className='mt-2 flex flex-col gap-1'>
                    {data.ai_tags_interest.slice(0, 15).map((t, i) => {
                      const color = CAT_COLORS[t.category] || '#64748b'
                      return (
                        <div key={i} className='flex items-center gap-2'>
                          <span
                            className='min-w-[60px] rounded-full px-2 py-0.5 text-center text-[10px] font-semibold whitespace-nowrap'
                            style={{ background: color + '15', color }}
                          >
                            {t.category}
                          </span>
                          <Text size={12}>{t.tag_name}</Text>
                          <Text size={10} color={TextColor.SECONDARY}>
                            {t.count}件
                          </Text>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </AiInsideArea>
            )}
          </>
        </div>
      </div>
    </AreaContent>
  )
}

interface AccessCustomerModalInnerProps {
  siteId: number
  visitorId: string
}

const AccessCustomerModalInner = ({ siteId, visitorId }: AccessCustomerModalInnerProps) => {
  const { data, isLoading } = useCustomerBehaviorAnalyticsGetApi({
    site_id: siteId,
    visitor_id: visitorId
  })

  return (
    <ModalContent maxKeep type={ModalContentType.NO_FRAME}>
      {isLoading || !data ? (
        <AreaContent>
          <ListFormLoading />
        </AreaContent>
      ) : (
        <BehaviorAnalysisModalInner
          data={{
            _customer_id: null,
            ...data
          }}
          siteId={siteId}
        />
      )}
    </ModalContent>
  )
}

const AccessCustomerModal = () => {
  const { modal, showing, hide } = useContext(AccessCustomerModalContext)

  return (
    <Modal show={showing} type={'large'}>
      <ModalHeader text={'行動分析'} onClickClose={hide} />
      {modal.showing && (
        <AccessCustomerModalInner siteId={modal.siteId} visitorId={modal.visitorId} />
      )}
      <ModalFooter>
        <Button color={'secondary'} size={'large'} onClick={hide}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default AccessCustomerModal
